import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { PermissionManager, OpuPermissions } from "@/lib/permissions";
import { toast } from "sonner";

// ─── Read current user's permissions ─────────────────────────────────

export const usePermissions = () => {
  const { user, isLoading: isAuthLoading } = useAuth();

  const query = useQuery({
    queryKey: ["user-permissions", user?.id],
    queryFn: async (): Promise<number> => {
      const { data, error } = await supabase.rpc("get_user_permissions", {
        p_user_id: user!.id,
      });

      if (error) {
        console.error("[usePermissions] RPC error:", error);
        return 0;
      }

      return (data as number) ?? 0;
    },
    enabled: !!user?.id && !isAuthLoading,
    staleTime: 1000 * 60 * 5,
    retry: 1,
  });

  const pm = new PermissionManager(query.data ?? 0);

  return {
    /** Raw permissions value */
    permissions: query.data ?? 0,
    /** PermissionManager instance for easy checks */
    pm,
    /** Check a single permission */
    has: (perm: number) => pm.has(perm),
    /** Check any of the given permissions */
    hasAny: (...perms: number[]) => pm.hasAny(...perms),
    /** Check all of the given permissions */
    hasAll: (...perms: number[]) => pm.hasAll(...perms),
    /** Whether the user is an administrator */
    isAdmin: pm.has(OpuPermissions.VIEW_ADMIN_PANEL),
    /** Whether the user is a super admin */
    isSuperAdmin: pm.has(OpuPermissions.ADMINISTRATOR),
    /** Loading state – true until both auth AND permissions are resolved */
    isLoading: isAuthLoading || query.isLoading,
    /** Error state */
    isError: query.isError,
  };
};

// ─── Fetch a specific user's permissions (admin) ─────────────────────

export const useUserPermissions = (userId: string | undefined) => {
  return useQuery({
    queryKey: ["user-permissions-admin", userId],
    queryFn: async (): Promise<{ id: string; user_id: string; permissions: number } | null> => {
      if (!userId) return null;

      const { data, error } = await (supabase as any)
        .from("user_permissions")
        .select("id, user_id, permissions")
        .eq("user_id", userId)
        .maybeSingle();

      if (error) {
        console.error("[useUserPermissions] Error:", error);
        return null;
      }

      return data;
    },
    enabled: !!userId,
    staleTime: 1000 * 60 * 2,
  });
};

// ─── Update a user's permissions (admin) ─────────────────────────────

export const useUpdatePermissions = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      userId,
      permissions,
    }: {
      userId: string;
      permissions: number;
    }) => {
      const { error } = await (supabase as any)
        .from("user_permissions")
        .upsert(
          {
            user_id: userId,
            permissions,
            updated_at: new Date().toISOString(),
          },
          { onConflict: "user_id" }
        );

      if (error) throw error;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ["user-permissions"] });
      queryClient.invalidateQueries({ queryKey: ["user-permissions-admin", variables.userId] });
      queryClient.invalidateQueries({ queryKey: ["admin-access"] });
      toast.success("Berechtigungen aktualisiert");
    },
    onError: () => {
      toast.error("Fehler beim Aktualisieren der Berechtigungen");
    },
  });
};

// ─── Add permission bits to a user ───────────────────────────────────

export const useGrantPermission = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      userId,
      permission,
    }: {
      userId: string;
      permission: number;
    }) => {
      const currentPerms = await supabase.rpc("get_user_permissions", {
        p_user_id: userId,
      });

      const currentValue = (currentPerms.data as number) ?? 0;
      const newValue = currentValue | permission;

      const { error } = await (supabase as any)
        .from("user_permissions")
        .upsert(
          {
            user_id: userId,
            permissions: newValue,
            updated_at: new Date().toISOString(),
          },
          { onConflict: "user_id" }
        );

      if (error) throw error;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ["user-permissions"] });
      queryClient.invalidateQueries({ queryKey: ["user-permissions-admin", variables.userId] });
      toast.success("Berechtigung hinzugefügt");
    },
    onError: () => {
      toast.error("Fehler beim Hinzufügen der Berechtigung");
    },
  });
};

// ─── Remove permission bits from a user ──────────────────────────────

export const useRevokePermission = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      userId,
      permission,
    }: {
      userId: string;
      permission: number;
    }) => {
      const currentPerms = await supabase.rpc("get_user_permissions", {
        p_user_id: userId,
      });

      const currentValue = (currentPerms.data as number) ?? 0;
      const newValue = currentValue & ~permission;

      const { error } = await (supabase as any)
        .from("user_permissions")
        .upsert(
          {
            user_id: userId,
            permissions: newValue,
            updated_at: new Date().toISOString(),
          },
          { onConflict: "user_id" }
        );

      if (error) throw error;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ["user-permissions"] });
      queryClient.invalidateQueries({ queryKey: ["user-permissions-admin", variables.userId] });
      toast.success("Berechtigung entfernt");
    },
    onError: () => {
      toast.error("Fehler beim Entfernen der Berechtigung");
    },
  });
};

// ─── Fetch all user permissions (admin overview) ─────────────────────

export const useAllUserPermissions = () => {
  return useQuery({
    queryKey: ["all-user-permissions"],
    queryFn: async () => {
      const { data, error } = await (supabase as any)
        .from("user_permissions")
        .select("*")
        .order("updated_at", { ascending: false });

      if (error) throw error;
      return data as Array<{
        id: string;
        user_id: string;
        permissions: number;
        created_at: string;
        updated_at: string;
      }>;
    },
  });
};
